import {useState, useEffect, useCallback} from 'react'
import { DocumentIcon, TrashIcon, DocumentDownloadIcon } from '@heroicons/react/solid'
import { useApi } from "../../hooks/useApi";
import { useDeleteConfirm } from "../../hooks/useDeleteConfirm";
import { useCancelConfirm } from "../../hooks/useCancelConfirm";
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import axios, { AxiosError } from "axios";
import noFileSelectImg from '../../assets/images/no_file_selected.png'
import {useFileView} from '../../providers/FileViewProvider';
import {Spinner} from '../../components/Spinner';
import { Transition } from '@headlessui/react'

export type TUploadResponse = {
  account_id: number,
  name: string,
  hash: string,
  size: number,
  date: string,
  expire: number,
  key: string,
  request_delete?: boolean,
};

export type TDeleteTarget = {
  name: string,
  hash: string
}

export type TToken = {
  id: number;
  token: string;
  expire: number;
  level: number;
}

//https://tailwindcomponents.com/component/file-upload-with-drop-on-and-preview
export const FileUpload = () => {
  const fileView = useFileView();
  const file = fileView.getFile();

  const [files, setFiles] = useState<File[]>([])
  const [uploadFiles, setUploadFiles] = useState<TUploadResponse[]>([])
  //https://www.bezkoder.com/react-hooks-file-upload/
  const [currentFile, setCurrentFile] = useState("");
  const [progress, setProgress] = useState(0);
  const [fileCount, setFileCount] = useState(0);
  const [accountId, setAccountId] = useState(0);
  const [target, setTarget] = useState<TDeleteTarget>();
  const { running, error, data, execute } = useApi();
  const navigate = useNavigate()
  const { deleteOk, reason, setDeleteOpen, setDeleteOk, setDeleteContent, DeleteConfirm } = useDeleteConfirm();
  const { cancelOk, setCancelOpen, setCancelOk, setCancelContent, CancelConfirm } = useCancelConfirm();
  const [level, setLevel] = useState(2);
  const [hash, setHash] = useState("");

  const api_server = localStorage.getItem("api_server") || "https://archives.mirai.ad.jp/";
  const api = `${api_server}api/arc`;

  useEffect(() => {
    // ローカルストレージからキーを指定して取得
    const api_key = localStorage.getItem("token") || "";
    setHash(localStorage.getItem("hash") || "");
    if (api_key) {
      const token: TToken = JSON.parse(atob(api_key));
      setAccountId(token.id);
      setLevel(token.level);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (deleteOk) {
      target && deleteRelatedFiles(target);
      setDeleteOk(false);
    }
    // eslint-disable-next-line
  },[deleteOk]);

  useEffect(() => {
    if (cancelOk) {
      target && deleteCancelRelatedFiles(target);
      setCancelOk(false);
    }
    // eslint-disable-next-line
  },[cancelOk]);

  useEffect(() => {
    setUploadFiles([]);
  },[file]);

  useEffect(() => {
    if (fileCount && uploadFiles && uploadFiles.length === fileCount) {
      console.log('related_files更新開始:', {
        fileCount,
        uploadFilesLength: uploadFiles.length,
        currentRelatedFiles: file?.related_files,
        uploadFiles: uploadFiles
      });

      const newRelatedFiles = [...(file?.related_files || [])];
      const newUploadFiles = [...uploadFiles];

      newUploadFiles.forEach((newValue) => {
        const isDuplicate = newRelatedFiles.some((oldValue) => oldValue.hash === newValue.hash);
        console.log('重複チェック:', {
          fileName: newValue.name,
          isDuplicate,
          hash: newValue.hash,
          existingHashes: newRelatedFiles.map(f => f.hash)
        });
        if (!isDuplicate) {
          newRelatedFiles.push(newValue);
        }
      });

      const params = {
        related_files: newRelatedFiles,
        upload_files: newUploadFiles
      };

      console.log('API更新パラメータ:', params);
      execute({ url: `/v1/update_file/${file.id}/`, params });
      setFileCount(0);
      setUploadFiles([]);
    }
  }, [fileCount, uploadFiles, file?.related_files, file?.id, execute]);

  useEffect(() => {
    if (data) {
      console.log('API更新後のデータ:', data);
      fileView.updateFiles(file);
    }
  }, [data]);

  const OnUpload = async (files: File[]) => {
    console.log('OnUpload開始:', files.map(f => ({ name: f.name, size: f.size })));
    setFileCount(files.length);
    setUploadFiles([]);
    setProgress(0);

    // ファイルを1つずつ順番にアップロード
    for (const file of files) {
      try {
        console.log('ファイルアップロード開始:', file.name);
        await uploadFile(file, (progressEvent: ProgressEvent) => {
          setCurrentFile(file.name);
          const per = progressEvent.lengthComputable ? Math.round((100 * progressEvent.loaded) / progressEvent.total) : 0;
          setProgress(per);
        });
        console.log('ファイルアップロード完了:', file.name);
      } catch (error: unknown) {
        console.error(`アップロード失敗 ${file.name}:`, error);
        if (axios.isAxiosError(error) && error.response?.status === 409) {
          alert(`${file.name}のアップロードに失敗しました。ファイルが処理中のため、しばらく待ってから再試行してください。`);
        } else {
          alert(`${file.name}のアップロードに失敗しました。`);
        }
      }
    }
  };

  const uploadFile = async (f: File, onUploadProgress: (progressEvent: ProgressEvent) => void) => {
    console.log('uploadFile開始:', f.name);
    const origin = window.location.origin || "";
    const api = `https://archives.mirai.ad.jp/api/arc`;
    const token = localStorage.getItem("token") || "";
    let formData = new FormData();
    formData.append("file", f);
    setCurrentFile(f.name);

    const maxRetries = 3;
    let retryCount = 0;

    while (retryCount < maxRetries) {
      try {
        console.log(`アップロード試行 ${retryCount + 1}/${maxRetries}:`, f.name);
        const response = await axios.post(`${api}/file_upload/`,
          formData, {
          headers: {
            "X-API-Key": token,
            "X-File-ID": file.id.toString(),
            "Content-Type": "multipart/form-data",
            "Accept": "application/json",
          },
          onUploadProgress,
          timeout: 30000,
        });

        if (response.data.files) {
          console.log('アップロードレスポンス:', response.data.files);
          response.data.files.forEach((res:TUploadResponse) => {
            setUploadFiles((currentUploadFiles) => {
              console.log('uploadFiles更新前:', currentUploadFiles);
              let newUploadFiles = [...currentUploadFiles];
              newUploadFiles.push(res);
              console.log('uploadFiles更新後:', newUploadFiles);
              return newUploadFiles;
            });
            deleteItem(res.name);
          });
          return;
        }
      } catch (error: unknown) {
        console.error(`アップロード試行 ${retryCount + 1} 失敗:`, error);
        if (axios.isAxiosError(error) && error.response?.status === 409) {
          retryCount++;
          if (retryCount === maxRetries) {
            throw new Error(`ファイルのアップロードに失敗しました。${f.name}`);
          }
          await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
          continue;
        }
        throw error;
      }
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    setProgress(0);
    acceptedFiles && acceptedFiles.forEach((file : File) => {
      setFiles((files) => {
        let newFiles: File[] = [];
        let dupeFlag = false;
        files.forEach(v => {
          if (file.name === v.name) {
            dupeFlag = true;
          }
          newFiles.push(v);
        });
        if ( ! dupeFlag ) newFiles.push(file);
        return newFiles;
      });
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const deleteItem = useCallback((name: string) => {
    setFiles((files) => {
      let newFiles: File[] = [];
      files.forEach(v => {
        if (name !== v.name) {
          newFiles.push(v);
        }
      });
      return newFiles;
    });

  }, []);

  useEffect(() => {
    if (error?.status === 401) {
      // ローカルストレージから対象のキーに紐づく値を削除
      localStorage.removeItem("token");
      localStorage.removeItem("name");
      navigate('/');
    }
    // eslint-disable-next-line
  }, [error]);

    // DBのアップロード済み情報を更新する
  const saveRelatedFiles = () => {
    const newRelatedFiles = [...(file?.related_files || [])];
    uploadFiles.forEach((newValue) => {
      let dupeFlag = false;
      file?.related_files?.forEach((oldValue) => {
        if (oldValue.hash === newValue.hash) dupeFlag = true;
      });
      if (!dupeFlag) newRelatedFiles.push(newValue);
    });

    const params = {
      related_files: newRelatedFiles,
      upload_files: uploadFiles
    };
    execute({ url: `/v1/update_file/${file.id}/`, params });
  };

  // ファイルを指定して削除申請する
  const deleteRelatedFiles = (target: TDeleteTarget) => {
    console.log("*********** deleteRelatedFiles *******************");
    let newRelatedFiles:TUploadResponse[] = [];
    file?.related_files?.forEach((value) => {
      let newValue = { ...value };
      if (value.hash === target.hash) {
        newValue.request_delete = true; // 削除申請フラグをセット
      }
      newRelatedFiles.push(newValue);
    });
    file.related_files = newRelatedFiles;

    const delete_files: TDeleteTarget[] = [];
    delete_files.push(target);
    const params = {
      related_files: newRelatedFiles,
      reason,
      request_delete_files: delete_files
    };
    console.log(params);
    execute({ url: `/v1/update_file/${file.id}/`, params });
  };

  // ファイルを指定して削除申請を取り消しする
  const deleteCancelRelatedFiles = (target: TDeleteTarget) => {
    console.log("*********** deleteCancelRelatedFiles *******************");
    let newRelatedFiles:TUploadResponse[] = [];
    file?.related_files?.forEach((value) => {
      let newValue = { ...value };
      if (value.hash === target.hash) {
        delete newValue.request_delete; // 削除申請フラグを削除
      }
      newRelatedFiles.push(newValue);
    });
    file.related_files = newRelatedFiles;

    const delete_files: TDeleteTarget[] = [];
    delete_files.push(target);
    const params = {
      related_files: newRelatedFiles,
      cancel_delete_files: delete_files
    };
    console.log(params);
    execute({ url: `/v1/update_file/${file.id}/`, params });
  };

  //upload参考
  //https://chaika.hatenablog.com/entry/2020/08/07/160000
  //https://www.bezkoder.com/react-file-upload-axios/
  //https://www.bezkoder.com/react-hooks-file-upload/

  // 権限を確認する
  // 0:権限なし 1:固定リンク 2:他ユーザー 3:作成者 4:特権ユーザー
  // levels 0:"Baned", 1:"Guest", 2:"User", 3:"Audit", 4:"Manager", 5:"Admin"];
  const getPermission = () => {
    if (level === 5) return 4;  //特権ユーザー
    if (level === 4) {
      if (file.account_id === accountId) {
        return 3;  //Manager自身が作成者
      } else {
        return 4;  //Manager 他人が作成したものは特権ユーザーとして扱う
      }
    }
    if (level >= 2) {
      if (file.account_id === accountId) {
        return 3;  //作成者
      } else {
        return 2; //他ユーザー
      }
    }
    if (file.hash === hash) return 1;
    return 0; //権限なし
  }

  return (
<div>
  <div className="border-t border-gray-200 px-4 py-5 sm:px-6 min-h-screen">
    <Transition
      show={getPermission()>=2}
    >
    <main className="container mx-auto max-w-screen-lg h-full">
      {/* file upload modal */}
          <article tabIndex={0} aria-label="File Upload Modal" className="relative h-full flex flex-col bg-white shadow-xl rounded-md">
          {/* overlay */}
          {isDragActive && (
            <div id="overlay" className="w-full h-full absolute top-0 left-0 pointer-events-none z-50 flex flex-col items-center justify-center rounded-md">
              <i>
                <svg className="fill-current w-12 h-12 mb-3 text-blue-700" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                  <path d="M19.479 10.092c-.212-3.951-3.473-7.092-7.479-7.092-4.005 0-7.267 3.141-7.479 7.092-2.57.463-4.521 2.706-4.521 5.408 0 3.037 2.463 5.5 5.5 5.5h13c3.037 0 5.5-2.463 5.5-5.5 0-2.702-1.951-4.945-4.521-5.408zm-7.479-1.092l4 4h-3v4h-2v-4h-3l4-4z" />
                </svg>
              </i>
              <span className="text-xs text-blue-700">ファイルをドロップしてアップロードしてください。</span>
            </div>
          )}

        {/* scroll area */}
        <section className={`h-full overflow-auto p-8 w-full flex flex-col  ${isDragActive && `opacity-30`}`} >
          <div {...getRootProps()} >
            <input {...getInputProps()} />
            <header className="border-dashed border-2 border-gray-400 py-6 flex flex-col justify-center items-center bg-blue-50 hover:bg-blue-100">
              <p className="mb-3 font-semibold text-gray-900 flex flex-wrap justify-center">
                  <span className="text-gray-400 text-sm">アップロードするファイルを、<span className="text-blue-400">この枠内にドロップする</span>か、ファイルを選択してください。</span>
              </p>
                  <button id="button" className="mt-2 rounded-sm px-3 py-0 bg-gray-200 hover:bg-gray-300 focus:shadow-outline focus:outline-none">
                    <span className="text-sm">ファイルを選択する</span>
                  </button>

            </header>
            </div>
          <h1 className="pt-8 pb-3 font-semibold sm:text-sm text-gray-400">
            アップロードするファイル
          </h1>

          <ul id="gallery" className="flex flex-1 flex-wrap -m-1">
                {files.length === 0 ? (
                  <li id="empty" className="h-full w-full text-center flex flex-col items-center justify-center">
                    <img className="mx-auto w-32" src={noFileSelectImg} alt="no data" />
                    <span className="text-xs text-gray-500">ファイルが選択されていません。</span>
                  </li>)
                  : files.length && files.map((file, key) => {

                  const objectURL = URL.createObjectURL(file);
                  return file.type.match("image.*") ? (
                        <li className="block p-1 w-1/2 sm:w-1/3 md:w-1/4 lg:w-1/6 xl:w-1/8 h-24 " key={key} >
                          <article tabIndex={0} className="group hasImage w-full h-full rounded-md focus:outline-none focus:shadow-outline bg-gray-100 cursor-pointer relative text-transparent hover:text-white shadow-sm ">
                            <img alt={file.name} src={objectURL} className="img-preview w-full h-full sticky object-cover rounded-md bg-fixed " />

                            <section className="flex flex-col rounded-md text-xs break-words w-full h-full z-20 absolute top-0 py-2 px-3 hover:bg-black hover:opacity-60">
                            <h1 className="flex-1">{file.name}</h1>
                              <div className="flex">
                                <span className="p-1">
                                  <i>
                                    <svg className="fill-current w-4 h-4 ml-auto pt-" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                      <path d="M5 8.5c0-.828.672-1.5 1.5-1.5s1.5.672 1.5 1.5c0 .829-.672 1.5-1.5 1.5s-1.5-.671-1.5-1.5zm9 .5l-2.519 4-2.481-1.96-4 5.96h14l-5-8zm8-4v14h-20v-14h20zm2-2h-24v18h24v-18z" />
                                    </svg>
                                  </i>
                                </span>

                                <p className="p-1 size text-xs">
                              {
                                file.size > 1024
                                  ? file.size > 1048576
                                    ? Math.round(file.size / 1048576) + "mb" : Math.round(file.size / 1024) + "kb"
                                  : file.size + "b"
                              }
                                </p>
                              <button className="delete ml-auto focus:outline-none hover:bg-gray-300 p-1 rounded-md" onClick={() => deleteItem(file.name)} >
                                  <svg className="pointer-events-none fill-current w-4 h-4 ml-auto" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                    <path className="pointer-events-none" d="M3 6l3 18h12l3-18h-18zm19-4v2h-20v-2h5.711c.9 0 1.631-1.099 1.631-2h5.316c0 .901.73 2 1.631 2h5.711z" />
                                  </svg>
                                </button>
                              </div>
                            </section>
                          </article>
                        </li>
                      ):
                      (
                        <li className="block p-1 w-1/2 sm:w-1/3 md:w-1/4 lg:w-1/6 xl:w-1/8 h-24" key={key}>
                          <article tabIndex={0} className="group w-full h-full rounded-md focus:outline-none focus:shadow-outline elative bg-gray-100 cursor-pointer relative shadow-sm">
                          <img alt="upload preview" className="img-preview hidden w-full h-full sticky object-cover rounded-md bg-fixed" />

                          <section className="flex flex-col rounded-md text-xs break-words w-full h-full z-20 absolute top-0 py-2 px-3">
                            <h1 className="flex-1 group-hover:text-blue-800">{file.name}</h1>
                            <div className="flex">
                              <span className="p-1 text-blue-800">
                                <i>
                                  <svg className="fill-current w-4 h-4 ml-auto pt-1" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                    <path d="M15 2v5h5v15h-16v-20h11zm1-2h-14v24h20v-18l-6-6z" />
                                  </svg>
                                </i>
                              </span>
                              <p className="p-1 size text-xs text-gray-700">
                              {
                                file.size > 1024
                                  ? file.size > 1048576
                                    ? Math.round(file.size / 1048576) + "mb" : Math.round(file.size / 1024) + "kb"
                                  : file.size + "b"
                              }
                              </p>
                                <button className="delete ml-auto focus:outline-none hover:bg-gray-300 p-1 rounded-md text-gray-800" onClick={() => deleteItem(file.name) }>
                                <svg className="pointer-events-none fill-current w-4 h-4 ml-auto" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                  <path className="pointer-events-none" d="M3 6l3 18h12l3-18h-18zm19-4v2h-20v-2h5.711c.9 0 1.631-1.099 1.631-2h5.316c0 .901.73 2 1.631 2h5.711z" />
                                </svg>
                              </button>
                            </div>
                          </section>
                        </article>
                      </li>
                    );


                  })
                }
          </ul>
        </section>

        {/* progress bar */}
        {progress && files.length && progress < 100 ?(
          <div className="m-10">
            <div className="mb-1 flex justify-between">
              <span className="text-base text-blue-500 font-medium">{currentFile}</span>
              <span className="text-sm font-medium text-blue-500">{progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div className="bg-blue-500 h-2.5 rounded-full" style={{ width: `${progress}%` }}></div>
            </div>
          </div>
        ): <></>}

      {running && <Spinner text="Now File API processing..." />}
      {error && <span className="mt-4 inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-pink-100 text-pink-800 ml-4 mb-4">
                  API実行中にエラーが発生しました。データは更新されていません。
                </span>
      }

        {/* sticky footer */}
        <footer className={`flex justify-end px-8 pb-8 pt-4 ${isDragActive && `opacity-30`}`}>
          { progress && files.length ?
            (
            <button id="submit" className="rounded px-3 py-1 bg-gray-500 text-white focus:shadow-outline focus:outline-none cursor-default">
              <span className="text-sm" >アップロード</span>
            </button>
            ) : (
            <button id="submit" className="rounded px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white font-bold focus:shadow-outline focus:outline-none"
              onClick={() => { OnUpload(files) }}>
              <span className="text-sm" >アップロード</span>
            </button>
          )}
            <button id="cancel" className="ml-3 rounded px-3 py-1 hover:bg-gray-300 font-bold focus:shadow-outline focus:outline-none bg-gray-200"
              onClick={() => { setFiles([])}}>
                <span className="text-sm"  >キャンセル</span>
            </button>

        </footer>

      </article>
    </main>
</Transition>



    <div className="m-10">
          {file?.related_files?.length ?
            <dt className="text-sm font-medium text-gray-500 mt-4">
              アップロード済ファイル
            </dt>
            :
            <dt className="text-sm font-medium text-gray-500 mt-4">追加されたファイルはありません</dt>
          }

      <dd className="mt-1 text-sm text-gray-900">
        <ul className=" divide-y divide-gray-200">
              {file?.related_files?.map((value: TUploadResponse, index: number) => {
              return (
                <li className="pl-3 pr-4 py-3 flex items-center justify-between text-sm bg-gray-200 rounded-md mt-2 shadow-md hover:bg-gray-50" key={index}>
                  <div className="w-0 flex-1 flex items-center">
                    <DocumentIcon className="flex-shrink-0 h-5 w-5 text-gray-400" aria-hidden="true" />
                    {['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.txt', '.html'].some(ext =>
                      value.name.toLowerCase().endsWith(ext)
                    ) ? (
                      <a
                        href={`${api}/download/${file.id}/${value.hash}/f/${value.expire}/${value.key}/${accountId}?preview=true`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-2 flex-1 w-0 truncate text-blue-600 hover:text-blue-800 cursor-pointer"
                      >
                        {value.name}
                      </a>
                    ) : (
                      <span className="ml-2 flex-1 w-0 truncate">{value.name}</span>
                    )}
                  </div>
                  <div className="ml-2 text-gray-500">{value.date.replace(/-/g,"/")}</div>

                  <div className="mx-2 text-gray-500 inline-block">({
                    value.size > 1024
                      ? value.size> 1048576
                        ? Math.round(value.size / 1048576) + "mb" : Math.round(value.size / 1024) + "kb"
                      : value.size + "b"
                    })
                  </div>

                    {/* Header set Access-Control-Allow-Origin を .htaccessに設定"*" */}
                    <a href={`${api}/download/${file.id}/${value.hash}/f/${value.expire}/${value.key}/${accountId}`} >
                      <button className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-1 px-2 rounded inline-flex items-center">
                        <DocumentDownloadIcon className="h-6 w-4 mr-1" />
                        <span className="text-sm">ダウンロード</span>
                      </button>
                    </a>

                  {accountId !== value.account_id &&
                    (
                      <button className="bg-gray-500 text-white font-bold py-1 px-2 rounded inline-flex items-center ml-4 cursor-default ">
                        <TrashIcon className="h-6 w-4 mr-1" />
                        <span className="text-ms mr-1">
                          {value.request_delete ? "削除申請中" : "削除申請　"}
                        </span>
                      </button>
                    )
                  }
                  {accountId === value.account_id && value.request_delete &&
                    (
                      <button className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-1 px-2 rounded inline-flex items-center ml-4"
                        onClick={() => {
                          setTarget({ name: value.name, hash: value.hash });
                          setCancelContent(value.name);
                          setCancelOpen(true);
                        }}
                      >
                        <TrashIcon className="h-6 w-4 mr-1" />
                        <span className="text-sm mr-1">
                          削除申請中
                        </span>
                      </button>
                      )
                  }
                  {accountId === value.account_id && !value.request_delete &&
                    (
                      <button className="bg-red-600 hover:bg-red-700 text-white font-bold py-1 px-2 rounded inline-flex items-center ml-4"
                        onClick={() => {
                          setTarget({ name: value.name, hash: value.hash });
                          setDeleteContent(value.name);
                          setDeleteOpen(true);
                        }}
                      >
                        <TrashIcon className="h-6 w-4 mr-1" />
                        <span className="text-sm mr-1">
                          削除申請　
                        </span>
                      </button>
                    )
                  }
                  <DeleteConfirm />
                  <CancelConfirm />


                </li>
              )
            })
          }
        </ul>
      </dd>

    </div>


  </div>
</div>
  );
}

export default FileUpload;
